// Estado de los documentos de una vista (plantillas o empresa concreta).
// Se instancia una vez por companyId (ver App.jsx: <DocsProvider companyId=.../>)
// así que cada vista tiene su propia lista de documentos aislada.
import { createContext, useContext, useState, useCallback } from "react";
import { api } from "../api";

const DocsContext = createContext();

export function DocsProvider({ companyId, children }) {
  const [docs, setDocs]       = useState([]);
  const [trashDocs, setTrashDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trashLoading, setTrashLoading] = useState(false);
  const [error, setError]     = useState(null);

  const fetchDocs = useCallback(async (category) => {
    try {
      setLoading(true);
      const data = await api.getAll(category, companyId);
      setDocs(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError("No se pudo conectar al servidor. ¿Está corriendo en puerto 4000?");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Sube un documento nuevo; si esta vista es de una empresa, se etiqueta
  // con su company_id (sin él, el backend lo trata como plantilla global).
  const addDoc = async (formData) => {
    if (companyId) formData.append("company_id", companyId);
    const doc = await api.upload(formData);
    setDocs(prev => [doc, ...prev]);
    return doc;
  };

  const deleteDoc = async (id) => {
    const deleted = await api.remove(id);
    setDocs(prev => prev.filter(d => d.id !== id));
    if (!deleted?.error) {
      setTrashDocs(prev => [deleted, ...prev.filter((doc) => doc.id !== deleted.id)]);
    }
    return deleted;
  };

  const updateDoc = async (id, changes) => {
    const updated = await api.update(id, changes);
    setDocs(prev => prev.map(d => d.id === id ? updated : d));
    return updated;
  };

  const replaceFile = async (id, formData) => {
    const updated = await api.replace(id, formData);
    setDocs(prev => prev.map(d => d.id === id ? updated : d));
    return updated;
  };

  const moveDoc = async (id, category) => {
    const updated = await api.move(id, { category });
    setDocs(prev => prev.map(d => d.id === id ? updated : d));
    return updated;
  };

  const fetchTrash = async () => {
    try {
      setTrashLoading(true);
      const data = await api.getTrash(companyId);
      setTrashDocs(Array.isArray(data) ? data : []);
    } finally {
      setTrashLoading(false);
    }
  };

  const restoreDoc = async (id) => {
    const restored = await api.restore(id);
    if (!restored?.error) {
      setTrashDocs(prev => prev.filter((doc) => doc.id !== id));
      setDocs(prev => [restored, ...prev.filter((doc) => doc.id !== id)]);
    }
    return restored;
  };

  const removeDocForever = async (id) => {
    const result = await api.removeForever(id);
    if (!result?.error) {
      setTrashDocs(prev => prev.filter((doc) => doc.id !== id));
    }
    return result;
  };

  return (
    <DocsContext.Provider value={{ docs, trashDocs, loading, trashLoading, error, fetchDocs, fetchTrash, addDoc, deleteDoc, updateDoc, replaceFile, moveDoc, restoreDoc, removeDocForever }}>
      {children}
    </DocsContext.Provider>
  );
}

export const useDocs = () => useContext(DocsContext);
